import React, { useMemo } from "react";
import { Country, State, City } from "country-state-city";

/**
 * Country / State / City cascading dropdowns.
 * - Country: full world list
 * - State: only when country has states (USA, Canada, etc.)
 * - City: only when state has cities (mostly USA); free-text fallback otherwise
 *
 * Props:
 *   country: ISO country name (e.g. "United States")
 *   state:   state name (e.g. "California")
 *   city:    city name
 *   onChange({ country, state, city })
 *   testidPrefix
 */
export default function LocationSelect({ country, state, city, onChange, testidPrefix = "loc" }) {
  const countries = useMemo(() => Country.getAllCountries(), []);
  const countryObj = useMemo(
    () => countries.find(c => c.name === country),
    [countries, country]
  );
  const states = useMemo(
    () => (countryObj ? State.getStatesOfCountry(countryObj.isoCode) : []),
    [countryObj]
  );
  const stateObj = useMemo(
    () => states.find(s => s.name === state),
    [states, state]
  );
  const cities = useMemo(
    () => (countryObj && stateObj ? City.getCitiesOfState(countryObj.isoCode, stateObj.isoCode) : []),
    [countryObj, stateObj]
  );

  const isUSA = countryObj?.isoCode === "US";
  const hasStates = states.length > 0;
  const hasCities = cities.length > 0;

  return (
    <>
      <div>
        <div className="overline mb-2">Country *</div>
        <select
          data-testid={`${testidPrefix}-country`}
          value={country || ""}
          onChange={(e) => onChange({ country: e.target.value, state: "", city: "" })}
          className="gf-input"
        >
          <option value="">— Select country —</option>
          {countries.map(c => (
            <option key={c.isoCode} value={c.name}>{c.flag ? `${c.flag} ` : ""}{c.name}</option>
          ))}
        </select>
      </div>
      <div>
        <div className="overline mb-2">State / Region {isUSA ? "*" : ""}</div>
        {hasStates ? (
          <select
            data-testid={`${testidPrefix}-state`}
            value={state || ""}
            onChange={(e) => onChange({ country, state: e.target.value, city: "" })}
            className="gf-input"
          >
            <option value="">— Select state —</option>
            {states.map(s => (
              <option key={s.isoCode} value={s.name}>{s.name}</option>
            ))}
          </select>
        ) : (
          <input
            data-testid={`${testidPrefix}-state`}
            value={state || ""}
            onChange={(e) => onChange({ country, state: e.target.value, city })}
            placeholder={country ? "State / region (free text)" : "Pick country first"}
            disabled={!country}
            className="gf-input"
          />
        )}
      </div>
      <div className="sm:col-span-2">
        <div className="overline mb-2">City *</div>
        {hasCities ? (
          <select
            data-testid={`${testidPrefix}-city`}
            value={city || ""}
            onChange={(e) => onChange({ country, state, city: e.target.value })}
            className="gf-input"
          >
            <option value="">— Select city —</option>
            {cities.map(c => (
              <option key={`${c.name}-${c.stateCode}`} value={c.name}>{c.name}</option>
            ))}
          </select>
        ) : (
          <input
            data-testid={`${testidPrefix}-city`}
            value={city || ""}
            onChange={(e) => onChange({ country, state, city: e.target.value })}
            placeholder={country ? "City (free text)" : "Pick country first"}
            disabled={!country}
            className="gf-input"
          />
        )}
      </div>
    </>
  );
}
